import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

//comps
import { Box, Typography, TableCell, CircularProgress } from '@mui/material';
import FilterContainer from '../../components/layout/filterContainer';
import GlobalInput from '../../components/global/input';
import GlobalDropdown from '../../components/global/dropdown';
import FilterOption from '../../components/global/filterOption';
import GlobalButton from '../../components/global/button';
import DataGrid from '../../components/grid/dataGrid';
import ContentContainer from '../../components/layout/contentContainer';
import { CustomTableDropdownCell, CustomTableInputCell, CustomTableCheckboxCell } from '../../components/grid/CustomTableCell';

//icons
import { ReactComponent as FilterIcon } from '../../assets/icons/filter-icon.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus-icon.svg';

//constants
import { defaultFilerOptionsArr } from '../../constants';
import { colors } from '../../theme';

// api / model
import AccountApi from '../../api/account.api';
import { useAppSelector } from '../../hooks/hooks';
import { Account, AccountType } from '../../models/account';

const Accounts = () => {
    const appState = useAppSelector((state) => state.appState);
    const { school, schools, campuses } = appState;
    const _accountApi = new AccountApi();

    const [state, setState] = useState({
        accountTypes: [],
        filterObject: {
            bcitID: '',
            name: '',
            isActive: 'all',
            accountType: null,
            school: null,
            campus: null,
        },
        isClearAll: false,
    });

    const [accounts, setAccounts] = useState([]);
    const [resetPage, setResetPage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(true);

    const loadAccountData = async () => {
        const accounts = await _accountApi.GetAllAccounts();
        const accountTypes = await _accountApi.GetAccountTypes();
        setAccounts(accounts);
        setState({
            ...state,
            accountTypes: accountTypes.filter((type: AccountType) => type.accountTypeID !== 3),
        });
    };

    useEffect(() => {
        if (school?.id)
            loadAccountData()
                .catch((error) => toast.error('Error loading accounts'))
                .finally(() => setIsLoading(false));
    }, [appState]);

    const handleFilter = (name: string, value: any) => {
        setState({
            ...state,
            filterObject: {
                ...state.filterObject,
                [name]: value,
            },
        });
        setButtonDisabled(false);
    };

    useEffect(() => {
        if (!Object.values(state.filterObject).every((o) => o === '')) {
            setResetPage(true);
        } else {
            setResetPage(false);
            setButtonDisabled(true);
        }
    }, [state.filterObject]);

    const handleClearAll = () => {
        let filterObject = { ...state.filterObject };
        Object.keys(filterObject).forEach((i) => (filterObject[i] = ''));

        setState({
            ...state,
            filterObject: {
                ...filterObject,
                isActive: 'all',
                accountType: null,
                school: null,
                campus: null,
            },
            isClearAll: true,
        });
        setButtonDisabled(true);
    };

    useEffect(() => {
        setState({ ...state, isClearAll: false });
    }, [state.isClearAll]);

    const handleAddAccount = () => {
        const isInEdit = accounts.find((account) => account.isEditMode);
        if (!isInEdit) {
            setAccounts([
                {
                    accountType: null,
                    bcitID: '',
                    firstName: '',
                    id: 0,
                    isActive: null,
                    lastName: '',
                    schoolID: null,
                    isEditMode: true,
                },
                ...accounts,
            ]);
        }
        setResetPage(true);
    };

    const addAccount = async (account: any) => {
        try {
            const data = await _accountApi.AddNewAccount(account);
            //@ts-ignore
            setAccounts(accounts => accounts.map((a: Account) => a.isEditMode ? { ...account, id: data.id, isEditMode: false } : a));

            toast.success(`Account  ${account.firstName} ${account.lastName} successfully created.`);
        } catch (error) {
            toast.error("Error adding account");
            return true;
        }
    };

    const saveAccount = async (account: Account) : Promise<boolean> => {
        let hasEmptyField = false;

        const { isActive, schoolID, accountType, campusID, bcitID, id } = account;
        // Validation 1: Check for empty fields
        Object.values(account).find((o) => {
            if (o === '' || !o) hasEmptyField = true;
        });
        // Set isActive = true by default
        if (isActive === null) account = { ...account, isActive: true };
        if (accountType?.accountTypeID && schoolID) {
            // 1) Check if campusID is null for Admin (id:1), Superadmin (id:5), Facilities (id:6)
            if (accountType.accountTypeID === 1 || accountType.accountTypeID === 5 || accountType.accountTypeID === 6) {
                if (!campusID) hasEmptyField = true;
                else hasEmptyField = false;
            }
            // -> Set CampusID to null when account type is Read-only (id: 4) or Timetabler (id: 2)
            if (accountType.accountTypeID === 2 || accountType.accountTypeID === 4) {
                account = { ...account, campusID: null };
                hasEmptyField = false;
            }
        }
        if (hasEmptyField) {
            toast.warning('All fields must be filled.');
            return hasEmptyField;
        }

        // Validation 2: Check for invalid BCIT ID
        if (bcitID?.length < 9 || !bcitID?.startsWith('A0')) {
            toast.warning('BCIT ID is invalid.');
            return false;
        }
        // Validation 3: Check for A0 dupe
        else if (accounts.find((a) => a.bcitID === bcitID && a.id !== id)) {
            toast.warning(`An account with BCIT ID ${bcitID} already exists.`);
            return false;
        } 

        // API call
        if (!hasEmptyField) {
            if (account?.id === 0) {
                addAccount(account);
            } else {
                try {
                    await _accountApi.UpdateAccount(account);
                    toast.success(`Changes to account ${account.firstName} ${account.lastName} have been successfully saved.`);
                    return true;
                } catch (error) {
                    toast.error('Error updating account.');
                    return false;
                }
            }
        }
        return true;
    };

    // Below validation does not use accountTypes constants because naming conventions differ.
    const handleAdminType = (accountType: AccountType) => {
        // index 0 = admin, 4 = super admin
        const isAdmin = accountType?.accountTypeID === state.accountTypes[0]?.accountTypeID || accountType?.accountTypeID === state.accountTypes[4]?.accountTypeID;
        return isAdmin;
    };

    const handleFacilitiesType = (accountType: AccountType) => {
        // index 4 = facilities
        const isFacilities = accountType?.accountTypeID === state.accountTypes[3]?.accountTypeID;
        return isFacilities;
    };

    const getGridData = () => {
        let accountData = [...accounts];

        const { accountType, school, isActive, bcitID, name, campus } = state.filterObject;

        // filter by account type
        if (accountType?.accountTypeID) {
            accountData = accountData.filter((account) => account.accountType?.accountTypeID === accountType.accountTypeID);
        }

        // filter by school
        if (school) {
            accountData = accountData.filter((account) => account.schoolID === school.id);
        }

        //filter by isActive
        if (isActive !== 'all') {
            accountData = accountData.filter((account) => account.isActive === isActive);
        }

        //filter by bcitID
        if (bcitID) {
            accountData = accountData.filter((account) => account.bcitID.toLowerCase().includes(bcitID.toLocaleLowerCase()));
        }

        //filter by name
        if (name) {
            accountData = accountData.filter((account) => account.firstName.toLowerCase().includes(name.toLowerCase()) || account.lastName.toLowerCase().includes(name.toLowerCase()));
        }

        // filter by campus
        // Skip accounts with account types that do not require a campus
        if (campus) {
            accountData = accountData.filter((account) => {
                const isAdmin = handleAdminType(account.accountType);
                const isFacilities = handleFacilitiesType(account.accountType);
                if (!isAdmin && !isFacilities) return;
                else return account?.campusID === campus.id;
            });
        }

        return accountData;
    };

    const handleCancel = () => {
        const resetAccounts = accounts.slice(1);
        setAccounts(resetAccounts);
    };

    const renderAccountGrid = (data: any, onChange: any) => {
        const { accountType, schoolID, campusID, isActive } = data;

        const school = schools.find((s) => s.id === schoolID);
        const campus = campuses.find((c) => c.id === campusID);

        // Below validation does not use accountTypes constants because naming conventions differ.
        const isAdmin = handleAdminType(accountType);
        const isFacilities = handleFacilitiesType(accountType);

        const disabled = !isAdmin && !isFacilities;

        const onChangeDropdown = (name: any, value: any) => {
            if (name === 'schoolID' || name === 'campusID') {
                onChange(name, value?.id, data);
            }
            if (name === 'accountType') {
                onChange(name, value, data);
            }
        };

        return (
            <>
                <CustomTableDropdownCell
                    items={state.accountTypes}
                    data={data}
                    field="accountTypeID"
                    name="name"
                    onChange={onChangeDropdown}
                    value={accountType?.name}
                    placeholder={!accountType ? 'Select an account type' : accountType.name}
                    dataItemKey={'accountType'}
                    width="16%"
                />
                <CustomTableInputCell data={data} name="bcitID" onChange={onChange} width="5%" maxLength={9} placeholder={'A01123456'} toUpperCase={true} />
                <CustomTableInputCell data={data} name="firstName" onChange={onChange} width="15%" placeholder={'John'} />
                <CustomTableInputCell data={data} name="lastName" onChange={onChange} width="20%" placeholder={'Doe'} />
                <CustomTableDropdownCell
                    items={schools}
                    data={data}
                    field="id"
                    name="name"
                    onChange={onChangeDropdown}
                    value={school?.name}
                    placeholder={!school ? 'Select a school' : school.name}
                    dataItemKey={'schoolID'}
                    width="20%"
                />
                <CustomTableDropdownCell
                    items={campuses}
                    data={data}
                    field="id"
                    name="name"
                    onChange={onChangeDropdown}
                    value={disabled ? 'N/A' : campus?.name}
                    placeholder={disabled ? 'N/A' : !disabled && !campus ? 'Select a campus' : campus.name}
                    dataItemKey={'campusID'}
                    width="10%"
                    disabled={disabled}
                />
                <CustomTableCheckboxCell data={data} name="isActive" onChange={onChange} disabled={false} width="10%" isDefault={true} />
            </>
        );
    };
    
    return (
        <>
            <FilterContainer sx={{ justifyContent: 'space-between' }}>
                <FlexBox sx={{ alignItems: 'center' }}>
                    <GlobalDropdown
                        label="filter"
                        placeholder="Account Type"
                        icon={<FilterIcon />}
                        sx={{ mr: '5px' }}
                        width="120px"
                        data={state.accountTypes}
                        field="accountTypeID"
                        name="name"
                        dataItemKey={'accountType'}
                        onChange={handleFilter}
                        value={state.filterObject.accountType}
                    />
                    <GlobalDropdown
                        placeholder="School"
                        sx={{ mr: '5px', maxWidth: '150px' }}
                        width="120px"
                        data={schools}
                        field="id"
                        name="name"
                        dataItemKey={'school'}
                        onChange={handleFilter}
                        value={state.filterObject.school}
                    />
                    <GlobalDropdown
                        placeholder="Campus"
                        sx={{ mr: '10px', maxWidth: '150px' }}
                        width="120px"
                        data={campuses}
                        field="id"
                        name="name"
                        dataItemKey={'campus'}
                        onChange={handleFilter}
                        value={state.filterObject.campus}
                    />
                    <GlobalInput placeholder="BCIT ID" sx={{ maxWidth: '105px' }} value={state.filterObject.bcitID} name="bcitID" onChange={handleFilter} />
                    <GlobalInput placeholder="Name" sx={{ maxWidth: '135px' }} value={state.filterObject.name} name="name" onChange={handleFilter} />
                    <FilterOptionLabel variant="body2">Active</FilterOptionLabel>
                    <FilterOption
                        data={defaultFilerOptionsArr}
                        state={state.filterObject.isActive}
                        name={'label'}
                        onClickValue={'value'}
                        onOptionClick={(value: any) => handleFilter('isActive', value)}
                    />
                    <GlobalButton variant="round" text="clear all" secondary={'true'} endIcon={<RefreshIcon />} onClick={handleClearAll} disabled={buttonDisabled} />
                </FlexBox>
                <GlobalButton
                    text="add new account"
                    variant="contained"
                    startIcon={<PlusIcon />}
                    sx={{ maxHeight: '30px' }}
                    onClick={() => {
                        if (!Object.values(state.filterObject).every((o) => o === '')) {
                            handleClearAll();
                        }
                        handleAddAccount();
                    }}
                />
            </FilterContainer>
            {accounts?.length === 0 && isLoading ? (
                <ContentContainer padding={'0px 25px'}>
                    <FlexBox sx={{ justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={100} />
                    </FlexBox>
                </ContentContainer>
            ) : (
                <DataGrid
                    isEdit
                    topHeight={45}
                    data={getGridData()}
                    columns={accountGridColumns}
                    dataItemKey="id"
                    renderRows={renderAccountGrid}
                    onSaveAsync={saveAccount}
                    handleCancel={handleCancel}
                    resetPage={resetPage}
                    isClearSorting={state.isClearAll}
                    onHeaderClick={() => {
                        setButtonDisabled(false);
                    }}
                />
            )}
        </>
    );
};

export default Accounts;

const FlexBox = styled(Box)`
    && {
        display: flex;
        /* align-items: center; */
        flex-wrap: wrap;
        & > * {
            margin-right: 5px;
        }
    }
`;

const FilterOptionLabel = styled(Typography)`
    && {
        color: ${colors.grey.dark};
        font-weight: 700;
        margin-left: 5px;
    }
`;

const accountGridColumns = [
    {
        label: 'Account Type',
        disabled: true,
    },
    {
        label: 'BCIT ID',
        disabled: true,
    },
    {
        id: 'firstName',
        label: 'First Name',
    },
    {
        id: 'lastName',
        label: 'Last Name',
    },
    {
        label: 'School',
        disabled: true,
    },
    {
        label: 'Campus',
        disabled: true,
    },
    {
        label: 'Active',
        sx: {
            textAlign: 'center',
        },
        disabled: true,
    },
];
